import { useState, useEffect, useCallback, useRef } from 'react';
import { rpc, RpcError } from '@/api/client';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { Dialog } from '@/components/common/Dialog';
import { WeatherCard } from './WeatherCard';
import { LapTable } from './LapTable';
import { TheoreticalLapCard } from './TheoreticalLapCard';
import { SessionCornerView } from './SessionCornerView';
import { SessionSetupView } from './SessionSetupView';
import { useI18n } from '@/context/I18nContext';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import { useRouter } from '@/context/RouterContext';
import { ArrowLeft, Clock, CheckCircle2, CloudSun, Wrench, Trash2, Settings, ShieldAlert, CornerUpRight } from 'lucide-react';
import type { SessionDetailResponse, StatsResponse, TrackSectorBestsResponse } from '@/types/telemetry';

interface SessionDetailProps {
  sessionId: string;
  initialView?: string;
  initialTab?: string;
}

type SessionTab = 'overview' | 'corners' | 'weather' | 'setup' | 'manage';

const VALID_TABS: SessionTab[] = ['overview', 'corners', 'weather', 'setup', 'manage'];

const SECTOR_KEY_MAP: Record<number, string> = {
  1: 'sector1Ms', 2: 'sector2Ms', 3: 'sector3Ms',
  4: 'sector4Ms', 5: 'sector5Ms', 6: 'sector6Ms',
};

/** Resolve the initial tab from URL params (supports both old and new URL shapes). */
function resolveInitialTab(initialView?: string, initialTab?: string): SessionTab {
  if (VALID_TABS.includes(initialTab as SessionTab)) return initialTab as SessionTab;
  if (VALID_TABS.includes(initialView as SessionTab)) return initialView as SessionTab;
  if (initialTab === 'weather' || initialView === 'weather') return 'weather';
  return 'overview';
}

export function SessionDetail({ sessionId, initialView, initialTab }: SessionDetailProps) {
  const { t } = useI18n();
  const { user } = useAuth();
  const { showToast } = useToast();
  const { navigate } = useRouter();
  const [data, setData] = useState<SessionDetailResponse | null>(null);
  const [allTimeBestLapMs, setAllTimeBestLapMs] = useState(0);
  const [allTimeBestSector, setAllTimeBestSector] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Unified tab state
  const [activeTab, setActiveTabState] = useState<SessionTab>(
    resolveInitialTab(initialView, initialTab)
  );
  const tabButtonRefs = useRef<Partial<Record<SessionTab, HTMLButtonElement | null>>>({});

  // Build the URL with query params for current selection
  const buildUrl = useCallback((tab: SessionTab) => {
    const base = `#/session/${sessionId}`;
    const qp = new URLSearchParams();
    if (tab !== 'overview') qp.set('tab', tab);
    const qs = qp.toString();
    return qs ? `${base}?${qs}` : base;
  }, [sessionId]);

  const setActiveTab = useCallback((tab: SessionTab) => {
    setActiveTabState(tab);
    window.history.replaceState(null, '', buildUrl(tab));
  }, [buildUrl]);

  useEffect(() => {
    const btn = tabButtonRefs.current[activeTab];
    if (btn && typeof btn.scrollIntoView === 'function') {
      btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeTab, loading]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sessionData = await rpc<SessionDetailResponse>('GetSession', { sessionId });
      setData(sessionData);

      // Fetch all-time bests in parallel
      try {
        const driverName = sessionData.driver?.displayName || '';
        const trackName = sessionData.track?.displayName || sessionData.session?.trackId || '';
        const trackId = sessionData.session?.trackId || '';
        const driverId = sessionData.session?.driverId || '';

        const [statsData, sectorBestsData] = await Promise.all([
          rpc<StatsResponse>('GetStats', { query: `driver:${driverName} name:${trackName}` }),
          (trackId && driverId)
            ? rpc<TrackSectorBestsResponse>('GetTrackSectorBests', { trackId, driverId })
            : Promise.resolve({ sectors: [] } as TrackSectorBestsResponse),
        ]);

        const tb = (statsData.trackBests || []).find(t => t.trackName === trackName);
        if (tb) setAllTimeBestLapMs(tb.bestLapTimeMs || 0);

        const bestSectors: Record<string, number> = {};
        for (const s of (sectorBestsData.sectors || [])) {
          const k = SECTOR_KEY_MAP[s.sectorNumber];
          if (k) bestSectors[k] = s.bestDurationMs;
        }
        setAllTimeBestSector(bestSectors);
      } catch (e) {
        console.warn('Could not fetch all-time bests', e);
      }
    } catch (e: any) {
      const nf = e instanceof RpcError && e.isNotFound;
      setNotFound(nf);
      setError(nf ? 'The requested session could not be found.' : (e.message || t('session.failedToLoad')));
    } finally {
      setLoading(false);
    }
  }, [sessionId, t]);

  useEffect(() => {
    loadData();
    const onQueueCompleted = () => {
      loadData();
    };
    window.addEventListener('queue:completed', onQueueCompleted);
    return () => window.removeEventListener('queue:completed', onQueueCompleted);
  }, [loadData]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} isNotFound={notFound} />;
  if (!data) return <ErrorState message={t('session.noData')} />;

  const { session, driver, track, laps, weather, tyre } = data;
  const isOwner = !!user && (user.id === session?.userId || user.id === 'user-owner' || (user as any).isAdmin);

  const handleDeleteSession = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await rpc('DeleteSession', { id: sessionId });
      showToast(t('session.deleteSuccess'), 'success');
      setShowDeleteConfirm(false);
      navigate('#/');
    } catch (err: any) {
      console.error('Failed to delete session:', err);
      showToast(err.message || t('session.deleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      {/* Back */}
      <button
        onClick={() => window.history.back()}
        className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors mb-4 cursor-pointer"
      >
        <ArrowLeft size={14} />
        {t('common.back')}
      </button>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-9 h-9 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center">
          <Clock size={18} className="text-accent" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight">{track?.displayName || session?.trackId || ''}</h1>
            {data.analysisStatus === 'completed' ? (
              <span title={t('lapAnalysis.statusCompleted')} className="inline-flex items-center text-emerald-400">
                <CheckCircle2 size={15} />
              </span>
            ) : (
              <span title={t('lapAnalysis.statusPending')} className="inline-flex items-center text-amber-400">
                <Clock size={15} />
              </span>
            )}
          </div>
          <p className="text-xs text-text-muted mt-0.5">{t('session.title')}</p>
        </div>
      </div>

      {showDeleteConfirm && (
        <Dialog
          open={showDeleteConfirm}
          onClose={() => !deleting && setShowDeleteConfirm(false)}
          title={t('session.deleteConfirmTitle')}
          footer={
            <div className="flex items-center justify-end gap-3 w-full">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-bg-hover rounded-lg transition-colors cursor-pointer"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDeleteSession}
                disabled={deleting}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg transition-colors cursor-pointer shadow-sm"
              >
                <Trash2 size={14} />
                {deleting ? t('session.deleting') : t('session.delete')}
              </button>
            </div>
          }
        >
          <p className="text-xs text-text-secondary leading-relaxed">
            {t('session.deleteConfirmMessage')}
          </p>
        </Dialog>
      )}

      {/* Unified tabs: Overview | Corners | Weather | Setup | Manage */}
      <div
        role="tablist"
        aria-label={t('session.title')}
        className="flex items-center gap-1 mb-6 overflow-x-auto no-scrollbar -mx-5 px-5 sm:mx-0 sm:px-0"
        style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        <button
          ref={el => { tabButtonRefs.current.overview = el; }}
          role="tab"
          aria-selected={activeTab === 'overview'}
          onClick={() => setActiveTab('overview')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer shrink-0 whitespace-nowrap ${
            activeTab === 'overview'
              ? 'text-accent bg-accent/10'
              : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
          }`}
        >
          {t('session.chipOverview')}
        </button>
        <button
          ref={el => { tabButtonRefs.current.corners = el; }}
          role="tab"
          aria-selected={activeTab === 'corners'}
          onClick={() => setActiveTab('corners')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer shrink-0 whitespace-nowrap ${
            activeTab === 'corners'
              ? 'text-accent bg-accent/10'
              : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
          }`}
        >
          <CornerUpRight size={13} className="shrink-0" />
          {t('session.tabCorners')}
        </button>
        <button
          ref={el => { tabButtonRefs.current.weather = el; }}
          role="tab"
          aria-selected={activeTab === 'weather'}
          onClick={() => setActiveTab('weather')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer shrink-0 whitespace-nowrap ${
            activeTab === 'weather'
              ? 'text-accent bg-accent/10'
              : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
          }`}
        >
          <CloudSun size={13} className="shrink-0" />
          {t('session.tabWeather')}
        </button>
        <button
          ref={el => { tabButtonRefs.current.setup = el; }}
          role="tab"
          aria-selected={activeTab === 'setup'}
          onClick={() => setActiveTab('setup')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer shrink-0 whitespace-nowrap ${
            activeTab === 'setup'
              ? 'text-accent bg-accent/10'
              : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
          }`}
        >
          <Wrench size={12} className="shrink-0" />
          {t('session.tabSetup')}
        </button>
        {isOwner && (
          <button
            ref={el => { tabButtonRefs.current.manage = el; }}
            role="tab"
            aria-selected={activeTab === 'manage'}
            onClick={() => setActiveTab('manage')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer shrink-0 whitespace-nowrap ${
              activeTab === 'manage'
                ? 'text-accent bg-accent/10'
                : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
            }`}
          >
            <Settings size={12} className="shrink-0" />
            {t('session.tabManage')}
          </button>
        )}
      </div>

      {activeTab === 'overview' && (
        <>
          <TheoreticalLapCard
            sessionId={sessionId}
            laps={laps || []}
            allTimeBestLapMs={allTimeBestLapMs}
            allTimeBestSector={allTimeBestSector}
            trackDisplayName={track?.displayName || session?.trackId || ''}
            bestSectorsMs={data?.bestSectorsMs}
            amendedAnalysis={data?.amendedAnalysis}
            sessionAnalysisStatus={data?.analysisStatus}
          />
          <LapTable
            sessionId={sessionId}
            laps={laps || []}
            allTimeBestLapMs={allTimeBestLapMs}
            allTimeBestSector={allTimeBestSector}
            trackDisplayName={track?.displayName || session?.trackId || ''}
            tyreStartAge={tyre?.startAgeLaps}
            bestSectorsMs={data?.bestSectorsMs}
          />
        </>
      )}

      {activeTab === 'corners' && (
        <SessionCornerView
          sessionId={sessionId}
          laps={laps || []}
        />
      )}

      {activeTab === 'weather' && (
        <WeatherCard weather={weather || { condition: '' }} />
      )}

      {activeTab === 'setup' && (
        <SessionSetupView
          sessionId={sessionId}
          sessionDate={session?.date ?? ''}
          trackName={track?.displayName || session?.trackId || ''}
          driverName={driver?.displayName ?? ''}
        />
      )}

      {activeTab === 'manage' && isOwner && (
        <div className="max-w-2xl space-y-6">
          <div className="card p-5 border border-red-500/20 bg-red-500/5 rounded-xl space-y-4">
            <div className="flex items-center gap-2.5 text-red-400">
              <ShieldAlert size={18} />
              <h2 className="text-sm font-semibold">{t('session.dangerZone')}</h2>
            </div>
            <p className="text-xs text-text-secondary leading-relaxed">
              {t('session.deleteDescription')}
            </p>
            <div className="pt-2">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors cursor-pointer shadow-sm"
              >
                <Trash2 size={14} />
                {t('session.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
